/**
 * Template editor state hook.
 *
 * Owns the in-progress template definition, the section configurator selection,
 * preview state, and validation errors. Exposes a reducer-style API the builder
 * page components consume.
 *
 * Backend contract: docs/spec/platform-template-management.md
 * UI contract: docs/spec/display-template-builder.md (in the backend repo)
 */

import { useReducer, useCallback, useRef } from "react";
import * as TemplateService from "../services/TemplateService";

const initialState = {
  template: null,             // { id, name, specialty_tag, current_version_id, ... }
  definition: null,           // The TemplateDefinition JSON currently in the editor
  selectedSectionIndex: null, // null when no section selected; otherwise position
  pendingChanges: false,
  saving: false,
  previewState: { loading: false, error: null, note: null },
  validationErrors: [],       // [{ validator, message, section_id }]
  loadError: null,
};

const ACTIONS = {
  LOAD_START: "LOAD_START",
  LOAD_OK: "LOAD_OK",
  LOAD_FAIL: "LOAD_FAIL",
  NEW_TEMPLATE: "NEW_TEMPLATE",
  LOAD_DRAFT: "LOAD_DRAFT",
  SET_NAME: "SET_NAME",
  SELECT_SECTION: "SELECT_SECTION",
  ADD_SECTION: "ADD_SECTION",
  REMOVE_SECTION: "REMOVE_SECTION",
  REORDER_SECTIONS: "REORDER_SECTIONS",
  UPDATE_SECTION_AT: "UPDATE_SECTION_AT",
  SAVE_START: "SAVE_START",
  SAVE_OK: "SAVE_OK",
  SAVE_FAIL: "SAVE_FAIL",
  PREVIEW_START: "PREVIEW_START",
  PREVIEW_OK: "PREVIEW_OK",
  PREVIEW_FAIL: "PREVIEW_FAIL",
  CLEAR_DRAFT: "CLEAR_DRAFT",
  SET_SPECIALTY: "SET_SPECIALTY",
};

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD_START:
      return { ...state, loadError: null };
    case ACTIONS.LOAD_OK:
      return {
        ...state,
        template: action.payload.template,
        definition: action.payload.definition,
        selectedSectionIndex: null,
        pendingChanges: false,
        validationErrors: [],
        loadError: null,
      };
    case ACTIONS.LOAD_FAIL:
      return { ...state, loadError: action.payload };

    case ACTIONS.NEW_TEMPLATE:
      return {
        ...state,
        template: null,
        definition: emptyDefinition(action.payload),
        pendingChanges: true,
        validationErrors: [],
      };

    case ACTIONS.LOAD_DRAFT:
      // A generated/scaffolded definition that is not yet persisted. No template id.
      return {
        ...state,
        template: null,
        definition: action.payload,
        pendingChanges: true,
        validationErrors: [],
        selectedSectionIndex: null,
      };

    case ACTIONS.SET_NAME: {
      const next = {
        ...state.definition,
        template: { ...state.definition.template, name: action.payload },
      };
      return { ...state, definition: next, pendingChanges: true };
    }

    case ACTIONS.SELECT_SECTION:
      return { ...state, selectedSectionIndex: action.payload };

    case ACTIONS.ADD_SECTION: {
      const existing = state.definition.sections || [];
      // A template cannot contain the same section_id twice (backend UNIQUE
      // constraint on (template_version_id, section_id)). Re-adding an
      // already-present section is a no-op so the unique-key invariant that
      // dnd-kit/React keys rely on can never be broken.
      if (existing.some((s) => s.section_id === action.payload.section_id)) {
        return state;
      }
      const next = { ...state.definition };
      const appended = [...existing, action.payload];
      next.sections = appended.map((s, i) => ({ ...s, position: i }));
      return { ...state, definition: next, pendingChanges: true };
    }

    case ACTIONS.REMOVE_SECTION: {
      const idx = action.payload;
      const next = { ...state.definition };
      next.sections = next.sections.filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, position: i }));
      return {
        ...state,
        definition: next,
        pendingChanges: true,
        selectedSectionIndex: state.selectedSectionIndex === idx ? null : state.selectedSectionIndex,
      };
    }

    case ACTIONS.REORDER_SECTIONS: {
      const { from, to } = action.payload;
      const next = { ...state.definition };
      const sections = [...next.sections];
      const [moved] = sections.splice(from, 1);
      sections.splice(to, 0, moved);
      next.sections = sections.map((s, i) => ({ ...s, position: i }));
      return { ...state, definition: next, pendingChanges: true };
    }

    case ACTIONS.UPDATE_SECTION_AT: {
      const { index, partial } = action.payload;
      const next = { ...state.definition };
      next.sections = next.sections.map((s, i) =>
        i === index ? { ...s, ...partial } : s
      );
      return { ...state, definition: next, pendingChanges: true };
    }

    case ACTIONS.SAVE_START:
      return { ...state, saving: true, validationErrors: [] };
    case ACTIONS.SAVE_OK:
      return {
        ...state,
        saving: false,
        template: action.payload.template,
        definition: action.payload.definition,
        pendingChanges: false,
      };
    case ACTIONS.SAVE_FAIL:
      return { ...state, saving: false, validationErrors: action.payload };

    case ACTIONS.CLEAR_DRAFT:
      return {
        ...state,
        definition: { ...state.definition, sections: [] },
        selectedSectionIndex: null,
        pendingChanges: true,
        validationErrors: [],
      };

    case ACTIONS.SET_SPECIALTY: {
      const next = {
        ...state.definition,
        template: { ...state.definition.template, specialty_tag: action.payload },
      };
      return { ...state, definition: next, pendingChanges: true };
    }

    case ACTIONS.PREVIEW_START:
      return { ...state, previewState: { loading: true, error: null, note: null } };
    case ACTIONS.PREVIEW_OK:
      return { ...state, previewState: { loading: false, error: null, note: action.payload } };
    case ACTIONS.PREVIEW_FAIL:
      return { ...state, previewState: { loading: false, error: action.payload, note: null } };

    default:
      return state;
  }
}

function emptyDefinition({ name, specialty_tag, intended_visit_type }) {
  return {
    schema_version: "1.0",
    template: {
      name: name || "",
      specialty_tag: specialty_tag || "behavioral_health",
      intended_visit_type: intended_visit_type || "follow_up",
      description: null,
    },
    sections: [],
    metadata: {},
  };
}

const PREVIEW_TIMEOUT_MS = 90000;

export function useTemplateEditor() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const previewGenRef = useRef(0);

  const loadTemplate = useCallback(async (id) => {
    dispatch({ type: ACTIONS.LOAD_START });
    try {
      const { template, definition } = await TemplateService.getTemplate(id);
      dispatch({ type: ACTIONS.LOAD_OK, payload: { template, definition } });
    } catch (err) {
      dispatch({ type: ACTIONS.LOAD_FAIL, payload: err.message || String(err) });
    }
  }, []);

  const newTemplate = useCallback((opts = {}) => {
    dispatch({ type: ACTIONS.NEW_TEMPLATE, payload: opts });
  }, []);

  const setName = useCallback((name) => {
    dispatch({ type: ACTIONS.SET_NAME, payload: name });
  }, []);

  const setSpecialty = useCallback((tag) => {
    dispatch({ type: ACTIONS.SET_SPECIALTY, payload: tag });
  }, []);

  // Generate a draft template definition from a plain-language description.
  // The backend returns a definition only (no DB write); the admin refines and
  // then saves it via the normal Save path.
  const generateFromDescription = useCallback(async ({ description, specialty_tag, intended_visit_type, name }) => {
    const definition = await TemplateService.scaffoldTemplate({
      description,
      specialty_tag,
      intended_visit_type,
      name,
    });
    dispatch({ type: ACTIONS.LOAD_DRAFT, payload: definition });
  }, []);

  const cloneTemplate = useCallback(async (newName) => {
    if (!state.template?.id) return;
    const result = await TemplateService.cloneTemplate(state.template.id, { newName });
    // Load the freshly cloned template into the editor.
    if (result?.template?.id) {
      dispatch({ type: ACTIONS.LOAD_OK, payload: result });
    } else if (result?.id) {
      await loadTemplate(result.id);
    }
    return result;
  }, [state.template, loadTemplate]);

  const archiveTemplate = useCallback(async () => {
    if (!state.template?.id) return;
    await TemplateService.archiveTemplate(state.template.id);
  }, [state.template]);

  // Restore a prior version as the current content. The backend returns the
  // restored { template, definition }; load it into the editor so the canvas
  // swaps to the restored content (same LOAD_OK path clone uses).
  const restoreVersion = useCallback(async (versionNumber) => {
    if (!state.template?.id) return;
    const result = await TemplateService.restoreVersion(state.template.id, versionNumber);
    dispatch({ type: ACTIONS.LOAD_OK, payload: result });
    return result;
  }, [state.template]);

  const selectSection = useCallback((index) => {
    dispatch({ type: ACTIONS.SELECT_SECTION, payload: index });
  }, []);

  const addSection = useCallback((sectionEntry) => {
    // TODO(p3a-m1): use SectionLibrary defaults when adding (default_classification, default_style).
    const section = {
      section_id: sectionEntry.section_id,
      position: 0, // recomputed by reducer after splice if reordering happens
      classification: sectionEntry.default_classification,
      style: sectionEntry.default_style,
      amd_field_name: sectionEntry.default_amd_field_name || "",
      amd_control_id: null,
      section_overrides: {},
    };
    dispatch({ type: ACTIONS.ADD_SECTION, payload: section });
  }, []);

  const removeSection = useCallback((index) => {
    dispatch({ type: ACTIONS.REMOVE_SECTION, payload: index });
  }, []);

  const reorderSections = useCallback((from, to) => {
    dispatch({ type: ACTIONS.REORDER_SECTIONS, payload: { from, to } });
  }, []);

  const updateSectionAt = useCallback((index, partial) => {
    dispatch({ type: ACTIONS.UPDATE_SECTION_AT, payload: { index, partial } });
  }, []);

  const clearDraft = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_DRAFT });
  }, []);

  const save = useCallback(async () => {
    dispatch({ type: ACTIONS.SAVE_START });
    try {
      const definition = state.definition;
      if (state.template?.id) {
        const result = await TemplateService.updateTemplate(state.template.id, { definition });
        dispatch({ type: ACTIONS.SAVE_OK, payload: result });
      } else {
        const result = await TemplateService.createTemplate({
          name: definition.template.name,
          specialty_tag: definition.template.specialty_tag,
          definition,
        });
        dispatch({ type: ACTIONS.SAVE_OK, payload: result });
      }
    } catch (err) {
      const errors = err.response?.data?.validation_errors || [
        { validator: "Network", message: err.message || String(err) },
      ];
      dispatch({ type: ACTIONS.SAVE_FAIL, payload: errors });
    }
  }, [state.template, state.definition]);

  // Fork the current (possibly unsaved) draft into a brand-new named template.
  // Unlike clone (which requires an existing template.id), this works on any
  // draft. On success the editor switches to the newly created template.
  const saveAsNew = useCallback(async (name) => {
    dispatch({ type: ACTIONS.SAVE_START });
    try {
      const definition = {
        ...state.definition,
        template: { ...state.definition.template, name },
      };
      const result = await TemplateService.createTemplate({
        name,
        specialty_tag: definition.template.specialty_tag,
        definition,
      });
      dispatch({ type: ACTIONS.SAVE_OK, payload: result });
    } catch (err) {
      const errors = err.response?.data?.validation_errors || [
        { validator: "Network", message: err.message || String(err) },
      ];
      dispatch({ type: ACTIONS.SAVE_FAIL, payload: errors });
    }
  }, [state.definition]);

  const refreshPreview = useCallback(async (transcript) => {
    const gen = ++previewGenRef.current;
    dispatch({ type: ACTIONS.PREVIEW_START });
    let timeoutId;
    const withTimeout = (p) =>
      Promise.race([
        p,
        new Promise((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error("Preview timed out after 90s. Try again.")),
            PREVIEW_TIMEOUT_MS
          );
        }),
      ]);
    try {
      if (state.template?.id && !state.pendingChanges) {
        const result = await withTimeout(
          TemplateService.previewTemplate(state.template.id, { transcript })
        );
        if (gen !== previewGenRef.current) return; // superseded by a newer call
        dispatch({ type: ACTIONS.PREVIEW_OK, payload: result.note });
      } else if (state.definition) {
        const result = await withTimeout(
          TemplateService.previewDraft({ definition: state.definition, transcript })
        );
        if (gen !== previewGenRef.current) return; // superseded by a newer call
        dispatch({ type: ACTIONS.PREVIEW_OK, payload: result.note });
      }
    } catch (err) {
      if (gen !== previewGenRef.current) return; // superseded by a newer call
      dispatch({
        type: ACTIONS.PREVIEW_FAIL,
        payload: err?.response?.data?.detail || err?.message || String(err),
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }, [state.template, state.definition, state.pendingChanges]);

  return {
    state,
    loadTemplate,
    newTemplate,
    setName,
    setSpecialty,
    generateFromDescription,
    cloneTemplate,
    archiveTemplate,
    restoreVersion,
    selectSection,
    addSection,
    removeSection,
    reorderSections,
    updateSectionAt,
    clearDraft,
    save,
    saveAsNew,
    refreshPreview,
  };
}
